/**
 * The exit chapter's KPI links (ADR 9069): a card opens the list that holds exactly the rows it counts.
 *
 * Every link here is a table's own column filter in the address — `?<urlKey>.f.<column>=v:<value>,<value>`
 * (`DataTable.urlKey`) — on a page that already holds the list. The destination table is given an
 * EXPLICIT `urlKey` (its default is a hash of its columns, which a refactor would silently change), and
 * the card's href is built from the same constants, so the two cannot drift.
 *
 * What the link carries: the exit perimeter (`?market=`, `?season=`) — the card counted the rows of that
 * perimeter, so the list must read it too — and the one filter. Nothing of the page it leaves: the source
 * table's own sort and filters are not in the card.
 *
 * Pure, so the screens and the tests share one spelling.
 */

import { carriedSearch, rankedViewHref, tableFilterHref } from '@/planning';

import { CONSOLIDATION_DOORS_PATH, CONSOLIDATION_LANES_PATH, CONSOLIDATION_RUN_DOORS_PATH, CONSOLIDATION_RUN_LANES_PATH, doorPath } from './door-drill';
import { PLANS_PATH } from './drill-paths';
import { MARKET_PARAM, SEASON_PARAM } from './market-season';
import { OUTLET_PATH, OUTLET_RUN_PARAM, TRANSITION_RUN_PARAM, TRANSITIONS_PATH, MONTH_PARAM } from './lifecycle/scope';
import { CONSOLIDATION_OPTION_PARAM, CONSOLIDATION_RUN_ID_PARAM } from './scope';

/** The chapter's perimeter: the market and season the exit reads are narrowed by. */
export const EXIT_PERIMETER: readonly string[] = [MARKET_PARAM, SEASON_PARAM];

/**
 * `carry` (the third argument of every link below) names the parameters of the current address the destination reads too
 * and the card counted under: the exit perimeter, the market alone for a read that takes no season, or the consolidation
 * run and option. Always stated by the caller, so a page is never made to carry a parameter it does not read (the
 * URL-parameter census). Values are matched as the table's column hands them out (`String(column.value(row))`).
 * The builder is the shared one (`tableFilterHref`, `@/planning`).
 */

// ── the plan book (`/exit/plans`) ──────────────────────────────────────────────

/** The plan book's table in the address bar: `?plans.f.sign_off=…`, `?plans.f.state=…`. */
export const PLANS_TABLE_KEY = 'plans';

/**
 * The sign-off column's values — one per combination of the two approvals, each named for what is
 * MISSING (reqs/05 §1: they are separate). The column's cell, its filter and the cards that link to
 * it all read these.
 */
export const SIGN_OFF = {
  signedOff: 'exit.state.signedOff',
  awaitingFinance: 'exit.state.awaitingFinance',
  awaitingMerchant: 'exit.state.awaitingMerchant',
  awaitingBoth: 'exit.state.awaitingBoth',
} as const;

/** The plan book narrowed by its `sign_off` and/or `state` column; `carry` as in {@link tableFilterHref}. */
export function planBookHref(
  search: string,
  filters: { readonly sign_off?: readonly string[]; readonly state?: readonly string[] },
  carry: readonly string[] = EXIT_PERIMETER,
): string {
  return tableFilterHref(PLANS_PATH, search, PLANS_TABLE_KEY, filters as Readonly<Record<string, readonly string[]>>, { carry });
}

// ── the unsigned list (`/exit/plans/unsigned`) ─────────────────────────────────

const UNSIGNED_PATH = `${PLANS_PATH}/unsigned`;

/** The unsigned list's table in the address bar: `?unsigned.f.awaiting=…`. */
export const UNSIGNED_TABLE_KEY = 'unsigned';

/**
 * The unsigned list narrowed by its `awaiting` column. Its cell names the signature MISSING, and the
 * two chase lists overlap on purpose: «awaiting the merchant» holds the plans missing the merchant only
 * and those missing both.
 */
export function unsignedListHref(search: string, awaiting: readonly string[]): string {
  return tableFilterHref(UNSIGNED_PATH, search, UNSIGNED_TABLE_KEY, { awaiting }, { carry: EXIT_PERIMETER });
}

// ── the terminal position (`/exit/plans/terminal-position`) ─────────────────────

const TERMINAL_PATH = `${PLANS_PATH}/terminal-position`;

/** The terminal position's table in the address bar: `?terminal.f.standing=…`, `?terminal.f.as_of_week=…`. */
export const TERMINAL_TABLE_KEY = 'terminal';

/**
 * The terminal position narrowed by its `standing` column (the STORED verdict: `true` breaches its
 * target) or its `as_of_week` column.
 */
export function terminalPositionHref(
  search: string,
  filters: { readonly standing?: readonly string[]; readonly as_of_week?: readonly string[] },
): string {
  return tableFilterHref(TERMINAL_PATH, search, TERMINAL_TABLE_KEY, filters as Readonly<Record<string, readonly string[]>>, { carry: EXIT_PERIMETER });
}

// ── the cadence queue (`/exit/cadence`) ──────────────────────────────────────────

const CADENCE_PATH = '/exit/cadence';

/**
 * The cadence queue's full table in the address bar: `?proposals.f.state=…`, `?proposals.f.counterfactual_winner=…`.
 * Not `queue`: that is the approvals queue above it (`?queue.f.market=…`).
 */
export const CADENCE_TABLE_KEY = 'proposals';

/**
 * The cadence proposals narrowed by their `state` or `counterfactual_winner` column (the cadence reads no season). The
 * proposals table sits BELOW the approvals queue on the same page, so the link names it as its fragment
 * (`#proposals`, the id of the element that wraps the table): the card scrolls there, where the filter shows.
 */
export function cadenceQueueHref(
  search: string,
  filters: { readonly state?: readonly string[]; readonly counterfactual_winner?: readonly string[] },
): string {
  return tableFilterHref(CADENCE_PATH, search, CADENCE_TABLE_KEY, filters as Readonly<Record<string, readonly string[]>>, { carry: [MARKET_PARAM], fragment: CADENCE_TABLE_KEY });
}

// ── the consolidation pages (`/exit/consolidation-doors`, `/exit/consolidation-lanes`, one door) ─────

/** What the consolidation pages read from the address: the run, and the option the doors and lanes are of. */
const RUN_SCOPE: readonly string[] = [CONSOLIDATION_RUN_ID_PARAM, CONSOLIDATION_OPTION_PARAM];

/** The doors table in the address bar: `?doors.f.state=…` (`true`: the size run is broken). */
export const DOORS_TABLE_KEY = 'doors';
/** The lanes table in the address bar: `?lines.f.state=…` (`false`: the lane is withheld). */
export const LANES_TABLE_KEY = 'lines';
/** One door's position table (an option per row) in the address bar: `?position.f.state=…`. */
export const DOOR_POSITION_TABLE_KEY = 'position';

/** The option's doors narrowed by their run state. The run and the option are the page's perimeter; no market or season. */
export function consolidationDoorsHref(search: string, filters: { readonly state: readonly string[] }): string {
  return tableFilterHref(CONSOLIDATION_DOORS_PATH, search, DOORS_TABLE_KEY, filters, { carry: RUN_SCOPE });
}

/** The option's lanes narrowed by their state (`false`: withheld). */
export function consolidationLanesHref(search: string, filters: { readonly state: readonly string[] }): string {
  return tableFilterHref(CONSOLIDATION_LANES_PATH, search, LANES_TABLE_KEY, filters, { carry: RUN_SCOPE });
}

// A card whose rows no column filter expresses — an ABSENT figure (no ranking, no lane cost) or a stored flag next to the
// lane's state — narrows the page by a declared parameter that is the platform's own filter on the same read (ADR 9054,
// platform ADR 6194): the predicate that computes the card, not a second definition of it.

/** `?sellThroughAbsent=true` — the option's doors with no ranking (`sell_through_absent` on `GET /exit/consolidation/doors`). */
export const SELL_THROUGH_ABSENT_PARAM = 'sellThroughAbsent';
/** `?repairsSizeRun=true` — the lanes that repair a broken size run (`repairs_size_run` on `GET /exit/consolidation/lines`). */
export const REPAIRS_SIZE_RUN_PARAM = 'repairsSizeRun';
/** `?laneCostAbsent=true` — the lanes with no cost (`lane_cost_absent`). */
export const LANE_COST_ABSENT_PARAM = 'laneCostAbsent';
/** `?isProposed=true` — only the lanes the optimiser proposes (`is_proposed`); the lane book otherwise holds the withheld ones too. */
export const LANE_PROPOSED_PARAM = 'isProposed';

const isTrue = (search: string, name: string): boolean => new URLSearchParams(search).get(name) === 'true';

/** What narrows the doors page: only the doors with no ranking. Anything but `true` is every door. Pure. */
export interface DoorsNarrowing { readonly sellThroughAbsent: boolean }

export function readDoorsNarrowing(search: string): DoorsNarrowing {
  return { sellThroughAbsent: isTrue(search, SELL_THROUGH_ABSENT_PARAM) };
}

/** The narrowing as the doors read's own parameters — the page and its cards ask the platform in one spelling. */
export function doorsNarrowingQuery(narrowing: DoorsNarrowing): { readonly sell_through_absent?: boolean } {
  return narrowing.sellThroughAbsent ? { sell_through_absent: true } : {};
}

/** What narrows the lanes page. Anything but `true` is no narrowing on that field. Pure. */
export interface LanesNarrowing {
  readonly isProposed: boolean;
  readonly repairsSizeRun: boolean;
  readonly laneCostAbsent: boolean;
}

export function readLanesNarrowing(search: string): LanesNarrowing {
  return {
    isProposed: isTrue(search, LANE_PROPOSED_PARAM),
    repairsSizeRun: isTrue(search, REPAIRS_SIZE_RUN_PARAM),
    laneCostAbsent: isTrue(search, LANE_COST_ABSENT_PARAM),
  };
}

export function isLanesNarrowed(narrowing: LanesNarrowing): boolean {
  return narrowing.isProposed || narrowing.repairsSizeRun || narrowing.laneCostAbsent;
}

export function isDoorsNarrowed(narrowing: DoorsNarrowing): boolean {
  return narrowing.sellThroughAbsent;
}

/** The narrowing as the lines read's own parameters. */
export function lanesNarrowingQuery(narrowing: LanesNarrowing): {
  readonly is_proposed?: boolean; readonly repairs_size_run?: boolean; readonly lane_cost_absent?: boolean;
} {
  return {
    ...(narrowing.isProposed ? { is_proposed: true } : {}),
    ...(narrowing.repairsSizeRun ? { repairs_size_run: true } : {}),
    ...(narrowing.laneCostAbsent ? { lane_cost_absent: true } : {}),
  };
}

/** `path` under the `carry` parameters of `search` (stated by the caller: a page never carries one it does not read), plus the parameters of `names` set to `true`. Nothing else of the page travels. */
function narrowedHref(path: string, search: string, names: readonly string[], carry: readonly string[]): string {
  const params = new URLSearchParams(carriedSearch(search, carry));
  for (const name of names) params.set(name, 'true');
  const text = params.toString();
  return text === '' ? path : path + '?' + text;
}

/** The option's doors, every one: the run and option of `search`, no narrowing. */
export function consolidationDoorsWholeHref(search: string): string {
  return narrowedHref(CONSOLIDATION_DOORS_PATH, search, [], RUN_SCOPE);
}

/** The option's lanes, every one: the run and option of `search`, no narrowing. */
export function consolidationLanesWholeHref(search: string): string {
  return narrowedHref(CONSOLIDATION_LANES_PATH, search, [], RUN_SCOPE);
}

/** The option's doors with no ranking — «unranked». The run and option stay; the table's own filters do not. */
export function consolidationDoorsUnrankedHref(search: string): string {
  return narrowedHref(CONSOLIDATION_DOORS_PATH, search, [SELL_THROUGH_ABSENT_PARAM], RUN_SCOPE);
}

/** The option's proposed lanes that repair a broken size run — «serie riparate» counts them over the proposed lanes only. */
export function consolidationLanesRepairedHref(search: string): string {
  return narrowedHref(CONSOLIDATION_LANES_PATH, search, [REPAIRS_SIZE_RUN_PARAM, LANE_PROPOSED_PARAM], RUN_SCOPE);
}

/** The option's lanes with no cost — «senza costo» counts every lane of the book, proposed or withheld. */
export function consolidationLanesUncostedHref(search: string): string {
  return narrowedHref(CONSOLIDATION_LANES_PATH, search, [LANE_COST_ABSENT_PARAM], RUN_SCOPE);
}

/** One door's position table narrowed by its run state; the door is in the path and the run stays in the query. */
export function doorPositionHref(storeKey: string, search: string, filters: { readonly state: readonly string[] }): string {
  return tableFilterHref(doorPath(storeKey), search, DOOR_POSITION_TABLE_KEY, filters, { carry: [CONSOLIDATION_RUN_ID_PARAM] });
}

// ── the run-wide lists (`/exit/consolidation-run-doors`, `/exit/consolidation-run-lanes`) ────────────────────

// A card that counts the WHOLE run (every option) opens the list of the whole run: the platform's doors and lines reads take
// `run_id` alone, with no option (ADR 6194), and the same filters as the per-option pages plus the two a run-wide card needs.
// The page's perimeter is the run (`?runId=`, the consolidation run the planner chose); the filters are declared parameters
// (ADR 9054), each the platform's own predicate on the read — never a second definition of what the card counts.

/** `?runIsBroken=true` — the door positions whose size run is broken (`run_is_broken` on `GET /exit/consolidation/doors`). */
export const RUN_IS_BROKEN_PARAM = 'runIsBroken';
/** `?lanePriced=true` — the lanes that publish cost, revenue and net benefit all (`lane_priced` on `GET /exit/consolidation/lines`). */
export const LANE_PRICED_PARAM = 'lanePriced';

// A run-wide list is read ONE page at a time and states how many rows the filters match (`matching`, platform ADR 6194 section 5):
// it is never read to the end here, so a run of any size is linked — measured on 2026-09-24, CO's position holds 353,323
// option × door rows. The file walks the cursor on the click (see `consolidation-run-lists.tsx`).

/** What narrows the run's door positions. Anything but `true` is no narrowing on that field. Pure. */
export interface RunDoorsNarrowing { readonly runIsBroken: boolean; readonly sellThroughAbsent: boolean }

export function readRunDoorsNarrowing(search: string): RunDoorsNarrowing {
  return { runIsBroken: isTrue(search, RUN_IS_BROKEN_PARAM), sellThroughAbsent: isTrue(search, SELL_THROUGH_ABSENT_PARAM) };
}

/** The narrowing as the doors read's own parameters. */
export function runDoorsNarrowingQuery(narrowing: RunDoorsNarrowing): { readonly run_is_broken?: boolean; readonly sell_through_absent?: boolean } {
  return {
    ...(narrowing.runIsBroken ? { run_is_broken: true } : {}),
    ...(narrowing.sellThroughAbsent ? { sell_through_absent: true } : {}),
  };
}

/** What narrows the run's lanes. Anything but `true` is no narrowing on that field. Pure. */
export interface RunLanesNarrowing {
  readonly isProposed: boolean;
  readonly lanePriced: boolean;
  readonly laneCostAbsent: boolean;
  readonly repairsSizeRun: boolean;
}

export function readRunLanesNarrowing(search: string): RunLanesNarrowing {
  return {
    isProposed: isTrue(search, LANE_PROPOSED_PARAM),
    lanePriced: isTrue(search, LANE_PRICED_PARAM),
    laneCostAbsent: isTrue(search, LANE_COST_ABSENT_PARAM),
    repairsSizeRun: isTrue(search, REPAIRS_SIZE_RUN_PARAM),
  };
}

/** The narrowing as the lines read's own parameters. */
export function runLanesNarrowingQuery(narrowing: RunLanesNarrowing): {
  readonly is_proposed?: boolean; readonly lane_priced?: boolean; readonly lane_cost_absent?: boolean; readonly repairs_size_run?: boolean;
} {
  return {
    ...(narrowing.isProposed ? { is_proposed: true } : {}),
    ...(narrowing.lanePriced ? { lane_priced: true } : {}),
    ...(narrowing.laneCostAbsent ? { lane_cost_absent: true } : {}),
    ...(narrowing.repairsSizeRun ? { repairs_size_run: true } : {}),
  };
}

/** `path` for the run `runId`, with the parameters of `names` set to `true`. Nothing of the page it is opened from travels. */
function runListHref(path: string, runId: string, names: readonly string[]): string {
  const params = new URLSearchParams({ [CONSOLIDATION_RUN_ID_PARAM]: runId });
  for (const name of names) params.set(name, 'true');
  return `${path}?${params.toString()}`;
}

/** Every door position of the run, narrowed by the declared filters named (`RUN_IS_BROKEN_PARAM`, `SELL_THROUGH_ABSENT_PARAM`). */
export function consolidationRunDoorsHref(runId: string, narrowedBy: readonly string[] = []): string {
  return runListHref(CONSOLIDATION_RUN_DOORS_PATH, runId, narrowedBy);
}

/**
 * Every lane of the run, narrowed by the declared filters named (`LANE_PROPOSED_PARAM`, `LANE_PRICED_PARAM`,
 * `LANE_COST_ABSENT_PARAM`, `REPAIRS_SIZE_RUN_PARAM`).
 */
export function consolidationRunLanesHref(runId: string, narrowedBy: readonly string[] = []): string {
  return runListHref(CONSOLIDATION_RUN_LANES_PATH, runId, narrowedBy);
}

/**
 * The run's door positions with a broken size run — what the Estate's and the Cockpit's cards count. Those pages WRITE
 * `runIsBroken` into a link and never read it, so it is spelled as the literal here and not through `RUN_IS_BROKEN_PARAM`: the
 * URL-parameter census (ADR 9054) counts a named constant as a read, and a page that does not read the key must not declare
 * it (its header would draw a chip for a scope that conditions nothing). `kpi-links-run-wide.test.tsx` holds the two spellings equal.
 */
export function consolidationRunDoorsBrokenHref(runId: string): string {
  return runListHref(CONSOLIDATION_RUN_DOORS_PATH, runId, ['runIsBroken']);
}

/** The address `search` states, on `path`, with the one parameter `name` taken off — what a removable filter notice links to. The rest stays. */
export function withoutParamHref(path: string, search: string, name: string): string {
  const params = new URLSearchParams(search);
  params.delete(name);
  const text = params.toString();
  return text === '' ? path : `${path}?${text}`;
}

// ── the returns (`/exit/returns`) ─────────────────────────────────────────────

const RETURNS_PATH = '/exit/returns';

/** `?realisationAbsent=true` — the dispositions with no realisation per unit (`realisation_absent` on `GET /exit/returns`, ADR 6194). */
export const REALISATION_ABSENT_PARAM = 'realisationAbsent';

/** Whether the address narrows the returns to the dispositions with no realisation. Anything but `true` is every disposition. Pure. */
export function readRealisationAbsent(search: string): boolean {
  return isTrue(search, REALISATION_ABSENT_PARAM);
}

/** The dispositions with no realisation per unit — «senza valorizzazione». The returns read only the market, so only it travels. */
export function returnsUnpricedHref(search: string): string {
  return narrowedHref(RETURNS_PATH, search, [REALISATION_ABSENT_PARAM], [MARKET_PARAM]);
}

/** Every disposition of the market: the way back from the narrowed list. */
export function returnsWholeHref(search: string): string {
  return narrowedHref(RETURNS_PATH, search, [], [MARKET_PARAM]);
}

// ── the handover readiness (`/exit/handover`) ──────────────────────────────────

/** The readiness table in the address bar: `?handover.f.alert_lead=v:short`. */
export const HANDOVER_TABLE_KEY = 'handover';

/** The readiness rows narrowed by their alert-lead standing (`short`: under the minimum lead). The queue reads a market only. */
export function handoverHref(search: string, filters: { readonly alert_lead: readonly string[] }): string {
  return tableFilterHref('/exit/handover', search, HANDOVER_TABLE_KEY, filters, { carry: [MARKET_PARAM] });
}

// ── the D4 evidence (`/exit/evidence`) ─────────────────────────────────────────

/** The evidence table in the address bar: `?evidence.f.verdict=…`, `?evidence.f.at_constant_terminal_stock=…`. */
export const EVIDENCE_TABLE_KEY = 'evidence';

/** The verdicts that are a measured comparison — every verdict but «not evaluable» (`d4Verdict`). */
export const MEASURED_VERDICTS: readonly string[] = ['improved', 'not-improved'];

/** The evidence rows narrowed by verdict and/or by whether terminal stock was held constant (`true`). */
export function evidenceHref(
  search: string,
  filters: { readonly verdict?: readonly string[]; readonly at_constant_terminal_stock?: readonly string[] },
): string {
  return tableFilterHref('/exit/evidence', search, EVIDENCE_TABLE_KEY, filters as Readonly<Record<string, readonly string[]>>, { carry: EXIT_PERIMETER });
}

// ── the end-of-life pages (`/exit/lifecycle`, `/exit/lifecycle-outlet`) ─────────

/**
 * A ranked table narrowed by its `select` filters — `?f.<id>=<value>` (`useRankedCap`, no namespace on these pages). The
 * page's own scope stays: the market, the month and the run the card counted. Its previous rank and filters do not.
 */
function rankedFilterHref(path: string, search: string, runParam: string, filters: Readonly<Record<string, string>>): string {
  return rankedViewHref(path, search, filters, undefined, undefined, [MARKET_PARAM, MONTH_PARAM, runParam]);
}

/** «Open» on the transitions page: the recommendations with no answer yet (`decision` = `open`). */
export function transitionsDecisionHref(search: string, decision: 'open'): string {
  return rankedFilterHref(TRANSITIONS_PATH, search, TRANSITION_RUN_PARAM, { decision });
}

/** The outlet-exit page narrowed by its `decision` or `forced` filter. */
export function outletFilterHref(search: string, filters: { readonly decision?: 'open'; readonly forced?: 'forced' }): string {
  return rankedFilterHref(OUTLET_PATH, search, OUTLET_RUN_PARAM, filters as Readonly<Record<string, string>>);
}

// ── the stranded lots (`/exit/stranded`) ───────────────────────────────────────

/** The lots table in the address bar: `?stranded.f.verdict=v:stranded|undetermined|not-stranded`. */
export const STRANDED_TABLE_KEY = 'stranded';

/** The lots with one verdict. The three verdict cards count the same joined lots the table lists, so each is that filter. */
export function strandedVerdictHref(search: string, verdict: 'stranded' | 'undetermined' | 'not-stranded'): string {
  return tableFilterHref('/exit/stranded', search, STRANDED_TABLE_KEY, { verdict: [verdict] }, { carry: EXIT_PERIMETER });
}
